import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

type SearchParam = {
  key: string;
  value: string[];
};

const useSearchParamActions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const newParams = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams]
  );

  const getSearchParams = useCallback((): SearchParam[] => {
    const params: SearchParam[] = [];
    searchParams.forEach((value, key) => {
      const existingParam = params.find((param) => param.key === key);
      if (existingParam) {
        existingParam.value.push(value);
      } else {
        params.push({ key, value: [value] });
      }
    });
    return params;
  }, [searchParams]);

  const appendSearchParam = useCallback(
    (key: string, value: string, replace: boolean = true) => {
      if (replace) {
        newParams.set(key, value);
      } else {
        newParams.append(key, value);
      }

      setSearchParams(newParams);
    },
    [newParams, setSearchParams]
  );

  const appendMultipleSearchParams = useCallback(
    (params: Array<{ key: string; value: string; replace?: boolean }>) => {
      params.forEach(({ key, value, replace = true }) => {
        if (replace) {
          newParams.set(key, value);
        } else {
          newParams.append(key, value);
        }
      });

      setSearchParams(newParams);
    },
    [newParams, setSearchParams]
  );

  const removeSearchParam = useCallback(
    (key: string, value?: string) => {
      if (value !== undefined) {
        const values = newParams.getAll(key);
        newParams.delete(key);
        values.forEach((val) => {
          if (val !== value) {
            newParams.append(key, val);
          }
        });
      } else {
        newParams.delete(key);
      }

      setSearchParams(newParams);
    },
    [newParams, setSearchParams]
  );

  const removeAllSearchParams = useCallback(
    (keys: string[]) => {
      keys.forEach((key) => {
        newParams.delete(key);
      });

      setSearchParams(newParams);
    },
    [newParams, setSearchParams]
  );

  const getSearchParam = useCallback(
    (key: string): string | undefined => {
      return searchParams.get(key) || undefined;
    },
    [searchParams]
  );

  const getAllSearchParams = useCallback(
    (): SearchParam[] => getSearchParams(),
    [getSearchParams]
  );

  return {
    appendSearchParam,
    appendMultipleSearchParams,
    removeSearchParam,
    removeAllSearchParams,
    getSearchParam,
    getAllSearchParams,
    searchParams,
  };
};

export default useSearchParamActions;
